import { useMutation, useQuery } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { queryClient } from '~/apis';
import { http } from '~/apis/base';
import { updateDependentKeyAtom } from '~/pages/Performance/_counterfeit/@atoms/enforcementAnalysis';
import {
  PerfCommercePerformanceEnforcementProtocolsGetListingCountByTabRequest,
  PerfCommercePerformanceEnforcementProtocolsGetListingCountsOverTimeRequest,
  PerfCommercePerformanceEnforcementProtocolsGetRemovedDataRequest,
  PerfCommercePerformanceEnforcementProtocolsTopDataRequest,
} from '~/swagger/data-contracts';
import { PerformanceEnforcement } from '~/swagger/PerformanceEnforcement';
import { QueryKeyPerformance } from './index';

const QueryKey = [QueryKeyPerformance, 'enforcement'];
const performanceEnforcementApi = new PerformanceEnforcement(http);

export enum SectionKey {
  tabCount = 'tab-count',
  enfAnalysis = 'enf-analysis',
  removedData = 'removed-data',
  topCountry = 'top-country',
  topPlatform = 'top-platform',
  topAsset = 'top-asset',
  topInfringementType = 'top-infringement-type',
}

export const fetchEnforcementTabListQuery = () => {
  return queryClient.fetchQuery({
    queryKey: [...QueryKey, 'tab-list'],
    queryFn: async () => {
      const { data } = await performanceEnforcementApi.enforcementTabsCreate({});
      return data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useEnforcementTabListQuery = () => {
  return useQuery({
    queryKey: [...QueryKey, 'tab-list'],
    queryFn: async () => {
      const { data } = await performanceEnforcementApi.enforcementTabsCreate({});
      return data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useEnforcementTabCountQuery = (
  request: PerfCommercePerformanceEnforcementProtocolsGetListingCountByTabRequest
) => {
  const setDependentKey = useSetAtom(updateDependentKeyAtom);

  return useQuery({
    queryKey: [...QueryKey, SectionKey.tabCount, request],
    queryFn: async () => {
      const { data } = await performanceEnforcementApi.enforcementCountCreate(request);
      return data;
    },
    onSuccess: () => {
      //TODO-@morgan: remove
      setDependentKey(SectionKey.tabCount);
    },
  });
};

export const useEnforcementCountsOverTimeQuery = (
  request: PerfCommercePerformanceEnforcementProtocolsGetListingCountsOverTimeRequest
) => {
  const [dependentKey, setDependentKey] = useAtom(updateDependentKeyAtom);

  return useQuery({
    queryKey: [...QueryKey, SectionKey.enfAnalysis, request],
    queryFn: async () => {
      const { data } = await performanceEnforcementApi.enforcementCountsOverTimeCreate(request);
      return data;
    },
    select: (data) => ({
      ...data,
      chart_value_list: data.chart_value_list.map((item, index) => ({
        ...item,
        isFirstDataPoint: index === 0,
        isLastDataPoint: index === data.chart_value_list.length - 1,
      })),
    }),
    enabled: dependentKey.includes(SectionKey.tabCount),
    onSuccess: () => {
      //TODO-@morgan: remove
      setDependentKey(SectionKey.enfAnalysis);
    },
  });
};

export const useEnforcementCountsOverTimeCsvMutation = () =>
  useMutation(performanceEnforcementApi.enforcementExportCountsOverTimeCreate);

export const usePerformanceEnforcementRemovedCounts = (
  request: PerfCommercePerformanceEnforcementProtocolsGetRemovedDataRequest
) => {
  const [dependentKey, setDependentKey] = useAtom(updateDependentKeyAtom);

  return useQuery({
    queryKey: [...QueryKey, SectionKey.removedData, request],
    queryFn: async () => {
      const { data } = await performanceEnforcementApi.enforcementRemovedDataCreate(request);
      return data;
    },
    enabled: dependentKey.includes(SectionKey.enfAnalysis),
    onSuccess: () => {
      //TODO-@morgan: remove
      setDependentKey(SectionKey.removedData);
    },
  });
};

// Raw CSV
export const useEnforcementListingsRawCsvMutation = () =>
  useMutation({ mutationFn: performanceEnforcementApi.enforcementExportListingsRawCreate });

// Top countries
export const useEnforcementTopCountryQuery = (request: PerfCommercePerformanceEnforcementProtocolsTopDataRequest) => {
  const [dependentKey, setDependentKey] = useAtom(updateDependentKeyAtom);

  return useQuery({
    queryKey: [...QueryKey, SectionKey.topCountry, request],
    queryFn: async () => {
      const { data } = await performanceEnforcementApi.enforcementTopCountryCreate(request);
      return data;
    },
    enabled: dependentKey.includes(SectionKey.enfAnalysis),
    onSuccess: () => {
      //TODO-@morgan: remove
      setDependentKey(SectionKey.topCountry);
    },
  });
};

export const useEnforcementTopCountryCsvMutation = () =>
  useMutation(performanceEnforcementApi.enforcementExportTopCountryCreate);

// Top marketplaces
export const useEnforcementTopPlatformQuery = (request: PerfCommercePerformanceEnforcementProtocolsTopDataRequest) => {
  const [dependentKey, setDependentKey] = useAtom(updateDependentKeyAtom);

  return useQuery({
    queryKey: [...QueryKey, SectionKey.topPlatform, request],
    queryFn: async () => {
      const { data } = await performanceEnforcementApi.enforcementTopPlatformCreate(request);
      return data;
    },
    enabled: dependentKey.includes(SectionKey.enfAnalysis),
    onSuccess: () => {
      //TODO-@morgan: remove
      setDependentKey(SectionKey.topPlatform);
    },
  });
};

export const useEnforcementTopPlatformCsvMutation = () =>
  useMutation(performanceEnforcementApi.enforcementExportTopPlatformCreate);

// Top assets
export const useEnforcementTopAssetQuery = (request: PerfCommercePerformanceEnforcementProtocolsTopDataRequest) => {
  const [dependentKey, setDependentKey] = useAtom(updateDependentKeyAtom);

  return useQuery({
    queryKey: [...QueryKey, SectionKey.topAsset, request],
    queryFn: async () => {
      const { data } = await performanceEnforcementApi.enforcementTopAssetCreate(request);
      return data;
    },
    enabled: dependentKey.includes(SectionKey.enfAnalysis),
    onSuccess: () => {
      //TODO-@morgan: remove
      setDependentKey(SectionKey.topAsset);
    },
  });
};

export const useEnforcementTopAssetCsvMutation = () =>
  useMutation(performanceEnforcementApi.enforcementExportTopAssetCreate);

// Top infringement type
export const useEnforcementTopInfringementTypeQuery = (
  request: PerfCommercePerformanceEnforcementProtocolsTopDataRequest
) => {
  const [dependentKey, setDependentKey] = useAtom(updateDependentKeyAtom);

  return useQuery({
    queryKey: [...QueryKey, SectionKey.topInfringementType, request],
    queryFn: async () => {
      const { data } = await performanceEnforcementApi.enforcementTopInfringementTypeCreate(request);
      return data;
    },
    enabled: dependentKey.includes(SectionKey.enfAnalysis),
    onSuccess: () => {
      //TODO-@morgan: remove
      setDependentKey(SectionKey.topInfringementType);
    },
  });
};

export const useEnforcementTopInfringementTypeCsvMutation = () =>
  useMutation(performanceEnforcementApi.enforcementExportTopInfringementTypeCreate);
